import { useEffect, useRef } from 'react';
import type { StoreItem } from './StoreLocator.types';
import * as Styled from './StoreLocator.styled';

/* eslint-disable @typescript-eslint/no-explicit-any */
type GMap = any;
type GMarker = any;

interface GoogleMapsWindow extends Window {
  google?: { maps: any };
}

interface StoreMapProps {
  store: StoreItem | null;
  allStores: StoreItem[];
}

const MAP_ICON = '/Styles/Images/map-pin.png';
const STORE_ZOOM = 16;

const getGoogle = () =>
  (window as unknown as GoogleMapsWindow).google;

const clearMarkers = (markers: GMarker[]) => {
  markers.forEach((m: GMarker) => m.setMap(null));
  markers.length = 0;
};

export const StoreMap = ({ store, allStores }: StoreMapProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<GMap>(null);
  const markersRef = useRef<GMarker[]>([]);

  // Initialise map once
  useEffect(() => {
    const g = getGoogle();
    if (!containerRef.current || !g?.maps) return;

    mapRef.current = new g.maps.Map(containerRef.current, {
      center: { lat: 53.35, lng: -7.9 },
      zoom: 7,
      mapTypeControl: true,
      streetViewControl: true,
      zoomControl: true,
      fullscreenControl: true,
    });

    // Show all store pins on initial load
    showAllMarkers();
  }, []);

  const showAllMarkers = () => {
    const g = getGoogle();
    if (!mapRef.current || !g?.maps) return;

    clearMarkers(markersRef.current);

    const bounds = new g.maps.LatLngBounds();

    allStores.forEach(s => {
      const position = {
        lat: parseFloat(s.latitude),
        lng: parseFloat(s.longitude),
      };

      const marker = new g.maps.Marker({
        position,
        map: mapRef.current,
        icon: MAP_ICON,
      });

      markersRef.current.push(marker);
      bounds.extend(position);
    });

    if (allStores.length > 0) {
      mapRef.current.fitBounds(bounds);
    }
  };

  // React to store selection changes
  useEffect(() => {
    const g = getGoogle();
    if (!mapRef.current || !g?.maps) return;

    clearMarkers(markersRef.current);

    if (!store) {
      // No store selected — show all pins fitted to Ireland
      showAllMarkers();
      return;
    }

    // Single store selected — one pin, zoomed in
    const position = {
      lat: parseFloat(store.latitude),
      lng: parseFloat(store.longitude),
    };

    const marker = new g.maps.Marker({
      position,
      map: mapRef.current,
      icon: MAP_ICON,
    });

    markersRef.current.push(marker);
    mapRef.current.setCenter(position);
    mapRef.current.setZoom(STORE_ZOOM);
  }, [store?.id]);

  return <Styled.MapContainer ref={containerRef} />;
};
